import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { FiUsers, FiMessageCircle, FiUserPlus, FiCheck, FiX, FiLoader, FiUserMinus } from 'react-icons/fi';
import { API, getAvatarUrl } from '../../services/api';
import { useToast } from '../../ToastContext';

/**
 * ContactsView component.
 *
 * For brands/creators (role: brand | creator | admin):
 *   Shows the full contacts list (project-based + manually accepted).
 *
 * For creatives (role: creative):
 *   Shows their accepted contacts + pending incoming requests.
 *
 * Props:
 *   user                    – logged-in user object
 *   token                   – JWT auth token
 *   onOpenDirectChat(id)    – open a direct chat conversation
 *   onCreateBrief()         – navigate to brief creation
 *   onOpenProfile(userId)   – navigate to a user's public profile
 */
const ContactsView = ({ user, token, onOpenDirectChat, onCreateBrief, onOpenProfile }) => {
  const { addToast } = useToast();
  // For brand/creator: flat array. For creative: { contacts, requests }
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmRemoveId, setConfirmRemoveId] = useState(null);

  const isCreative = user?.role === 'creative';

  const load = useCallback(() => {
    if (!token || !user) return;
    setLoading(true);
    axios
      .get(`${API}/contacts`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setData(res.data))
      .catch(() => setData(isCreative ? { contacts: [], requests: [] } : []))
      .finally(() => setLoading(false));
  }, [token, user, isCreative]);

  useEffect(() => {
    load();
  }, [load]);

  const handleOpenChat = async (contact) => {
    if (!token || !onOpenDirectChat) return;
    try {
      if (contact.conversation_id) {
        onOpenDirectChat(contact.conversation_id);
        return;
      }
      const res = await axios.post(
        `${API}/conversations`,
        { other_user_id: contact.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onOpenDirectChat(res.data.id);
    } catch {
      addToast({ type: 'error', title: 'Error', description: 'No se pudo abrir el chat' });
    }
  };

  const handleAccept = async (requestId) => {
    try {
      await axios.put(
        `${API}/contacts/requests/${requestId}/accept`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      addToast({ type: 'success', title: 'Solicitud aceptada' });
      // Reload to get the accepted contact in the contacts list
      load();
    } catch (err) {
      addToast({ type: 'error', title: 'Error', description: err?.response?.data?.detail || 'No se pudo aceptar' });
    }
  };

  const handleDecline = async (requestId) => {
    try {
      await axios.put(
        `${API}/contacts/requests/${requestId}/decline`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      addToast({ type: 'info', title: 'Solicitud rechazada' });
      setData((prev) => ({ ...prev, requests: prev.requests.filter((r) => r.id !== requestId) }));
    } catch (err) {
      addToast({ type: 'error', title: 'Error', description: err?.response?.data?.detail || 'No se pudo rechazar' });
    }
  };

  const handleRemove = async (contactId) => {
    try {
      await axios.delete(`${API}/contacts/${contactId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      addToast({ type: 'info', title: 'Contacto eliminado' });
      if (isCreative) {
        setData((prev) => ({ ...prev, contacts: prev.contacts.filter((c) => c.id !== contactId) }));
      } else {
        setData((prev) => (Array.isArray(prev) ? prev.filter((c) => c.id !== contactId) : prev));
      }
    } catch (err) {
      addToast({ type: 'error', title: 'Error', description: err?.response?.data?.detail || 'No se pudo eliminar' });
    }
  };

  if (loading) {
    return (
      <div className="contacts-page">
        <div className="contacts-container">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)', padding: '40px 0' }}>
            <FiLoader className="spin" aria-hidden="true" /> Cargando contactos…
          </div>
        </div>
      </div>
    );
  }

  // ── Shared card renderers ─────────────────────────────────────────────────

  const renderContactCard = (contact) => (
    <motion.li
      key={contact.id}
      className="contacts-card"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.05 }}
    >
      <div
        className="contacts-card-avatar"
        style={onOpenProfile ? { cursor: 'pointer' } : {}}
        onClick={onOpenProfile ? () => onOpenProfile(contact.id) : undefined}
      >
        <img src={getAvatarUrl(contact.profile_image, contact.name)} alt={contact.name} />
      </div>
      <div className="contacts-card-info">
        <span
          className={`contacts-card-name${onOpenProfile ? ' contacts-card-name--link' : ''}`}
          onClick={onOpenProfile ? () => onOpenProfile(contact.id) : undefined}
        >
          {contact.name}
        </span>
        <span className="contacts-card-meta">
          {isCreative
            ? (contact.role === 'brand' ? 'Marca / Agencia' : 'Creador de contenido')
            : contact.source === 'manual'
              ? 'Contacto añadido manualmente'
              : `${contact.projects_count} ${contact.projects_count === 1 ? 'proyecto' : 'proyectos'} contigo`}
        </span>
      </div>
      <div className="contacts-card-actions">
        {confirmRemoveId === contact.id ? (
          <>
            <span className="contacts-card-confirm-text">¿Eliminar contacto?</span>
            <button
              type="button"
              className="contacts-card-confirm-yes"
              onClick={() => { setConfirmRemoveId(null); handleRemove(contact.id); }}
            >
              Sí, eliminar
            </button>
            <button
              type="button"
              className="contacts-card-confirm-cancel"
              onClick={() => setConfirmRemoveId(null)}
            >
              Cancelar
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className="btn-primary contacts-card-chat"
              onClick={() => handleOpenChat(contact)}
              title="Abrir chat"
            >
              <FiMessageCircle aria-hidden="true" />
              Abrir chat
            </button>
            <button
              type="button"
              className="contacts-card-remove"
              onClick={() => setConfirmRemoveId(contact.id)}
              title="Eliminar contacto"
            >
              <FiUserMinus aria-hidden="true" />
            </button>
          </>
        )}
      </div>
    </motion.li>
  );

  const renderRequestCard = (req) => {
    const fromUser = req.from_user || {};
    return (
      <motion.li
        key={req.id}
        className="contacts-card"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.05 }}
      >
        <div
          className="contacts-card-avatar"
          style={onOpenProfile ? { cursor: 'pointer' } : {}}
          onClick={onOpenProfile ? () => onOpenProfile(fromUser.id) : undefined}
        >
          <img src={getAvatarUrl(fromUser.profile_image, fromUser.name)} alt={fromUser.name} />
        </div>
        <div className="contacts-card-info">
          <span
            className={`contacts-card-name${onOpenProfile ? ' contacts-card-name--link' : ''}`}
            onClick={onOpenProfile ? () => onOpenProfile(fromUser.id) : undefined}
          >
            {fromUser.name || 'Usuario'}
          </span>
          <span className="contacts-card-meta">
            {fromUser.role === 'brand' ? 'Marca / Agencia' : 'Creador de contenido'}
          </span>
        </div>
        <div className="contacts-card-actions">
          <button
            type="button"
            className="btn-primary contacts-card-chat"
            onClick={() => handleAccept(req.id)}
            title="Aceptar solicitud"
          >
            <FiCheck aria-hidden="true" /> Aceptar
          </button>
          <button
            type="button"
            className="btn-ghost contacts-card-decline"
            onClick={() => handleDecline(req.id)}
            title="Rechazar solicitud"
          >
            <FiX aria-hidden="true" /> Rechazar
          </button>
        </div>
      </motion.li>
    );
  };

  // ── Creative view ─────────────────────────────────────────────────────────
  if (isCreative) {
    const creativeContacts = data?.contacts || [];
    const creativeRequests = data?.requests || [];
    const isEmpty = creativeContacts.length === 0 && creativeRequests.length === 0;

    return (
      <div className="contacts-page">
        <motion.div
          className="contacts-container"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <h1 className="contacts-title">
            <FiUsers aria-hidden="true" />
            Contactos
          </h1>
          <p className="contacts-subtitle">
            Tus contactos y solicitudes pendientes de marcas y creadores.
          </p>

          {isEmpty ? (
            <div className="contacts-empty">
              <div className="contacts-empty-icon"><FiUsers aria-hidden="true" /></div>
              <h2>Aún no tienes contactos</h2>
              <p>Cuando una marca o creador te añada como contacto y lo aceptes, aparecerá aquí.</p>
            </div>
          ) : (
            <>
              {creativeContacts.length > 0 && (
                <section className="contacts-section">
                  <h2 className="contacts-section-title">Mis contactos</h2>
                  <ul className="contacts-list">
                    {creativeContacts.map(renderContactCard)}
                  </ul>
                </section>
              )}

              {creativeRequests.length > 0 && (
                <section className="contacts-section">
                  <h2 className="contacts-section-title">
                    <FiUserPlus aria-hidden="true" />
                    Solicitudes pendientes
                    <span className="contacts-section-badge">{creativeRequests.length}</span>
                  </h2>
                  <ul className="contacts-list">
                    {creativeRequests.map(renderRequestCard)}
                  </ul>
                </section>
              )}
            </>
          )}
        </motion.div>
      </div>
    );
  }

  // ── Brand/creator view ────────────────────────────────────────────────────
  const contacts = Array.isArray(data) ? data : [];

  return (
    <div className="contacts-page">
      <motion.div
        className="contacts-container"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <h1 className="contacts-title">
          <FiUsers aria-hidden="true" />
          Contactos
        </h1>
        <p className="contacts-subtitle">
          Creativos con los que has trabajado o añadido manualmente. Abre un chat directo para seguir en contacto.
        </p>

        {contacts.length === 0 ? (
          <div className="contacts-empty">
            <div className="contacts-empty-icon"><FiUsers aria-hidden="true" /></div>
            <h2>Aún no tienes contactos</h2>
            <p>
              Puedes añadir creativos como contacto desde su perfil público, o trabajar con ellos en un proyecto para que aparezcan aquí automáticamente.
            </p>
            {onCreateBrief && (
              <button type="button" className="btn-primary contacts-empty-cta" onClick={onCreateBrief}>
                Crear mi primer brief
              </button>
            )}
          </div>
        ) : (
          <ul className="contacts-list">
            {contacts.map(renderContactCard)}
          </ul>
        )}
      </motion.div>
    </div>
  );
};

export default ContactsView;
